import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

// Consent for third-party content on the published site.
//
// Until 2026-09 the "cookie banner" was a notice: one button, a cookie nothing
// ever read, and every YouTube / Vimeo / Google Maps iframe loading on mount —
// the visitor's IP went to the provider before any choice was offered, and a
// site with the banner switched OFF loaded everything with no notice at all.
//
// One tri-state, stored in a first-party cookie:
//   unset   — no decision yet. Third-party iframes render as placeholders.
//   granted — load third-party content.
//   denied  — placeholders stay; each can still be loaded by its own button.
// plus per-PROVIDER session grants (`grantOnce`), which is what a placeholder's
// "load content from YouTube" button does: that one provider, for this browser
// tab (sessionStorage — the runtime unmounts its whole tree on every in-site
// navigation, so plain React state would ask again on each page), no cookie. It is also the ONLY consent path on a site whose operator
// disabled the banner — "banner off" must not mean "no consent needed".

export type ConsentState = "unset" | "granted" | "denied";

export const CONSENT_COOKIE = "veltric_consent";
/** The old notice cookie. Says nothing about third-party content ⇒ dropped. */
export const LEGACY_NOTICE_COOKIE = "veltric_cookie_notice";
const CONSENT_DAYS = 180;

type ConsentContextValue = {
  state: ConsentState;
  /** True when `host`'s content may load: global grant or a session grant. */
  isAllowed: (host: string) => boolean;
  accept: () => void;
  reject: () => void;
  /** Load ONE provider for this browser tab (sessionStorage). No cookie. */
  grantOnce: (host: string) => void;
  /** Art. 7(3): as easy as giving it. Clears the decision and every session grant. */
  withdraw: () => void;
  /**
   * There is something `withdraw` would take back: a stored decision or a
   * session grant. On a banner-less site that is the only time the legal
   * line's "Cookie settings" does anything a visitor can see.
   */
  canWithdraw: boolean;
  /** Bumped by `withdraw`: the banner moves focus to itself when it re-opens. */
  reopenSignal: number;
  /** Operator's privacy page for the placeholders' link (else the platform's). */
  privacyPolicyUrl: string | undefined;
};

const noop = (): void => {};
/** Outside the provider (error shells): nothing is allowed, nothing can be changed. */
const INERT: ConsentContextValue = {
  state: "unset",
  isAllowed: () => false,
  accept: noop,
  reject: noop,
  grantOnce: noop,
  withdraw: noop,
  canWithdraw: false,
  reopenSignal: 0,
  privacyPolicyUrl: undefined,
};

const ConsentContext = createContext<ConsentContextValue>(INERT);

export function readCookie(name: string): string | null {
  const hit = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return hit === undefined ? null : hit.slice(name.length + 1);
}

function writeCookie(name: string, value: string, days: number): void {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // `Secure` only on https: on http (localhost, e2e) a Secure cookie is
  // silently refused and the choice would never stick.
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax${secure}`;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
}

const SESSION_GRANTS_KEY = "veltric_consent_session";

function readSessionGrants(): ReadonlySet<string> {
  try {
    const raw = window.sessionStorage.getItem(SESSION_GRANTS_KEY);
    const list: unknown = raw === null ? [] : JSON.parse(raw);
    return new Set(Array.isArray(list) ? list.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set(); // storage blocked / garbage ⇒ nothing granted
  }
}

function writeSessionGrants(grants: ReadonlySet<string>): void {
  try {
    if (grants.size === 0) window.sessionStorage.removeItem(SESSION_GRANTS_KEY);
    else window.sessionStorage.setItem(SESSION_GRANTS_KEY, JSON.stringify([...grants]));
  } catch {
    // storage blocked: the grant still holds for this mount
  }
}

function readStoredState(): ConsentState {
  const raw = readCookie(CONSENT_COOKIE);
  return raw === "granted" || raw === "denied" ? raw : "unset";
}

export function WBConsentProvider({
  children,
  privacyPolicyUrl,
}: {
  children: ReactNode;
  privacyPolicyUrl?: string | undefined;
}) {
  const [state, setState] = useState<ConsentState>(readStoredState);
  const [sessionGrants, setSessionGrants] = useState<ReadonlySet<string>>(readSessionGrants);
  const [reopenSignal, setReopenSignal] = useState(0);

  // The old cookie only recorded "saw the notice". It is not consent to
  // third-party content, so it is removed and the banner asks once more.
  useEffect(() => {
    if (readCookie(LEGACY_NOTICE_COOKIE) !== null) deleteCookie(LEGACY_NOTICE_COOKIE);
  }, []);

  const accept = useCallback(() => {
    writeCookie(CONSENT_COOKIE, "granted", CONSENT_DAYS);
    setState("granted");
  }, []);
  const reject = useCallback(() => {
    writeCookie(CONSENT_COOKIE, "denied", CONSENT_DAYS);
    setState("denied");
  }, []);
  const grantOnce = useCallback((host: string) => {
    setSessionGrants((prev) => {
      const next = new Set(prev).add(host.toLowerCase());
      writeSessionGrants(next);
      return next;
    });
  }, []);
  const withdraw = useCallback(() => {
    deleteCookie(CONSENT_COOKIE);
    setState("unset");
    setSessionGrants(new Set());
    writeSessionGrants(new Set());
    setReopenSignal((n) => n + 1);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      isAllowed: (host) => state === "granted" || sessionGrants.has(host.toLowerCase()),
      accept,
      reject,
      grantOnce,
      withdraw,
      canWithdraw: state !== "unset" || sessionGrants.size > 0,
      reopenSignal,
      privacyPolicyUrl,
    }),
    [state, sessionGrants, accept, reject, grantOnce, withdraw, reopenSignal, privacyPolicyUrl],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useWBConsent(): ConsentContextValue {
  return useContext(ConsentContext);
}
